import React from "react";

import {
  Box,
  Badge,
  Stack,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import {
  PlusCircleIcon,
  Users01Icon,
  DotsVerticalIcon,
  Trash02Icon,
  Typography,
  UserUp01Icon,
  Edit01Icon,
  BankNote03Icon,
  ChevronDownDoubleIcon,
  ChevronUpDoubleIcon,
} from "components";
import { Permissions } from "configs";
import { error } from "configs/colors";
import { initLanContent } from "helpers";
import usePermission from "hooks/usePermission";
import { i18n } from "translations";
import { rem } from "utils";

import { ActionType } from "./constants";
import Manager from "./Manager";
import { ActionProps, ExtendInfo } from "./OrgTree.types";

interface Props {
  title: string;
  id: number;
  isRootNode?: boolean;
  hasChildren?: boolean;
  isCollapsed?: boolean;
  isShowEmployees?: boolean;
  actions?: ActionProps;
  extendInfo?: ExtendInfo;
  onToggleCollapse?: () => void;
  onToggleShowEmployees?: () => void;
}

const Node = (props: Props) => {
  const {
    title = "",
    isRootNode = false,
    id = 0,
    actions = {} as ActionProps,
    extendInfo = {} as ExtendInfo,
    hasChildren = false,
    isCollapsed,
    isShowEmployees,
    onToggleCollapse,
    onToggleShowEmployees,
  } = props;
  const { canAccess } = usePermission();
  const { nodeData = {} as ExtendInfo["nodeData"], totalEmployee = 0 } =
    extendInfo;
  const {
    deptManagers = [],
    fetchedEmployee = false,
    costCenterCode,
    code,
  } = nodeData;

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClickNode = (
    node: ExtendInfo["nodeData"],
    action: ActionType
  ) => {
    actions.onClickNode(node, action);
    handleClose();
  };

  return (
    <>
      <Box
        id={String(id || "")}
        padding="10px 0"
        borderRadius={rem(8)}
        display="inline-block"
        boxShadow="0px 10px 15px -3px rgba(0, 0, 0, 0.1)"
        border={`1px solid ${grey[200]}`}
      >
        <Stack alignItems="center" gap={rem(4)} minWidth="100px">
          {hasChildren && (
            <IconButton
              aria-label={isCollapsed ? "expand" : "collapse"}
              size="small"
              onClick={onToggleCollapse}
            >
              {isCollapsed ? (
                <ChevronDownDoubleIcon fontSize="small" color="info" />
              ) : (
                <ChevronUpDoubleIcon fontSize="small" color="info" />
              )}
            </IconButton>
          )}
          <Stack
            justifyContent="center"
            paddingLeft={rem(10)}
            paddingRight={rem(10)}
          >
            <Typography
              variant={isRootNode ? "h5" : "body1"}
              sx={{
                color: isRootNode ? "primary.main" : "text.primary",
              }}
            >
              <strong style={{ whiteSpace: "nowrap" }}>
                {title} <br />
                {code && `(${code})`}
              </strong>
              {costCenterCode && (
                <>
                  <br />
                  <Stack
                    direction="row"
                    alignItems="center"
                    columnGap="8px"
                    justifyContent="center"
                  >
                    <BankNote03Icon fontSize="small" />
                    <span style={{ fontSize: "14px" }}>
                      {costCenterCode || ""}
                    </span>
                  </Stack>
                </>
              )}
            </Typography>
            <Stack alignItems="center">
              {deptManagers
                .filter((item) => item?.employee)
                .map((item) => (
                  <Stack key={item.employee.id}>
                    <Manager
                      item={item}
                      handleClickTrash={() =>
                        handleClickNode(
                          {
                            ...nodeData,
                            extendInfo: {
                              ...nodeData?.extendInfo,
                              managerId: item.id,
                              employeeId: item.employee.id,
                              role: item.role,
                            } as ExtendInfo["nodeData"]["extendInfo"],
                          },
                          ActionType.DELETE_DEPT_MANAGER
                        )
                      }
                    />
                  </Stack>
                ))}
            </Stack>
          </Stack>
          <Divider sx={{ width: "100%", margin: "0 0 8px 0" }} />
          <Stack
            direction="row"
            spacing={rem(8)}
            justifyContent="space-around"
            width="100%"
          >
            {!isRootNode && canAccess(Permissions.OrgChart.ShowAllEmployee) && (
              <Badge
                showZero={fetchedEmployee}
                badgeContent={totalEmployee}
                color="warning"
              >
                <Tooltip
                  title={
                    isShowEmployees
                      ? initLanContent([
                          "Ẩn danh sách nhân viên",
                          "Hide employee list",
                        ])
                      : i18n.t("showAllEmployees")
                  }
                  arrow
                >
                  <IconButton
                    aria-label="list-user"
                    size="small"
                    color="secondary"
                    onClick={() => {
                      onToggleShowEmployees?.();
                      handleClickNode(nodeData, ActionType.SHOW_EMPLOYEE);
                    }}
                  >
                    <Users01Icon />
                  </IconButton>
                </Tooltip>
              </Badge>
            )}

            {canAccess(Permissions.OrgChart.AddNode) && (
              <Tooltip title={i18n.t("addNode")} arrow>
                <IconButton
                  aria-label="add"
                  size="small"
                  color="success"
                  onClick={() => handleClickNode(nodeData, ActionType.ADD_DEPT)}
                >
                  <PlusCircleIcon />
                </IconButton>
              </Tooltip>
            )}

            {!isRootNode && (
              <Tooltip title={i18n.t("more")} arrow>
                <IconButton
                  aria-label="menu"
                  size="small"
                  color="default"
                  onClick={handleClick}
                >
                  <DotsVerticalIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Box>
      <Menu
        id="demo-positioned-menu"
        aria-labelledby="demo-positioned-button"
        open={open}
        onClose={handleClose}
        anchorEl={anchorEl}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: "visible",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: 1.5,
              "&:before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {canAccess(Permissions.OrgChart.AddManager) && (
          <MenuItem
            onClick={() => handleClickNode(nodeData, ActionType.ADD_EMPLOYEE)}
          >
            <ListItemIcon>
              <UserUp01Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{i18n.t("addManager")}</ListItemText>
          </MenuItem>
        )}
        {canAccess(Permissions.OrgChart.UpdateNode) && (
          <MenuItem
            onClick={() => handleClickNode(nodeData, ActionType.UPDATE_DEPT)}
          >
            <ListItemIcon>
              <Edit01Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{i18n.t("updateNode")}</ListItemText>
          </MenuItem>
        )}
        {canAccess(Permissions.OrgChart.RemoveNode) && (
          <MenuItem
            onClick={() => handleClickNode(nodeData, ActionType.DELETE_DEPT)}
          >
            <ListItemIcon>
              <Trash02Icon fontSize="small" sx={{ color: error[600] }} />
            </ListItemIcon>
            <Typography color="error.600">{i18n.t("removeNode")}</Typography>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default Node;
